export type ExtendedWindowType = Window &
  typeof globalThis & {
    cdn: string;
    withState: string[];
    withoutState: string[];
    tooltips: { [key: string]: string };
    heading: string;
    nurse: {
      heading: string;
      description: string;
    };
    allied: {
      heading: string;
      description: string;
    };
    metaIn: string;
    hcSuffix: string;
    noQueries: {
      title: string;
      heading: string;
      description: string;
    };
  };

export const ReplaceGradeIcons = () => {
  const gradeIcons = document.querySelectorAll<HTMLImageElement>('.v-niche-grade');
  const nicheGrades = document.body.dataset.nicheGrades
    ? JSON.parse(document.body.dataset.nicheGrades)
    : null;

  gradeIcons.forEach(gradeIcon => {
    const gradeField = gradeIcon.dataset?.gradeField;

    let grade = gradeIcon.getAttribute('alt');

    if (gradeField && nicheGrades) {
      grade = nicheGrades[gradeField];
    }

    if (grade) {
      gradeIcon.setAttribute(
        'src',
        `${(window as ExtendedWindowType).cdn}images/${grade.toLowerCase()}.png`
      );
    } else {
      gradeIcon.style.setProperty('display', 'none');
    }
  });
};

const capitalize = (str: string) => {
  if (!str) return '';
  str.trim();
  const words = str.split(' ');

  const capitalizedWords = words.map(word => {
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });

  return capitalizedWords.join(' ');
};

export const ReplaceHeading = (heading: HTMLElement) => {
  const city = document.body.dataset.name ? document.body.dataset.name : '';
  const state = document.body.dataset.state ? document.body.dataset.state : '';

  if (heading.innerText.includes('{{NAME}}')) {
    if (state) {
      heading.innerText = heading.innerText
        .replace('{{NAME}}', city.toUpperCase())
        .replace('{{STATE}}', state.toUpperCase());
    } else {
      heading.innerText = heading.innerText
        .replace('{{NAME}}', city.toUpperCase())
        .replace(', {{STATE}}', '');
    }
  } else if (heading.innerText.includes('{{STATE}}')) {
    if (state) {
      heading.innerText = heading.innerText.replace('{{STATE}}', state.toUpperCase());
    }
  } else {
    if (heading.innerText.includes('{{Name}}')) {
      if (state) {
        heading.innerText = heading.innerText
          .replace('{{Name}}', `${capitalize(city)}`)
          .replace('{{State}}', `${capitalize(state)}`);
      } else {
        heading.innerText = heading.innerText
          .replace('{{Name}}', `${capitalize(city)}`)
          .replace(', {{State}}', '');
      }
    } else if (heading.innerText.includes('{{State}}')) {
      heading.innerText = heading.innerText.replace('{{State}}', `${capitalize(state)}`);
    }
  }
};

export const ConditionalDisplay = (id: string) => {
  const cityPage = document.body.dataset.state;
  const _window = window as ExtendedWindowType;

  if (cityPage) {
    return _window.withoutState.includes(id);
  }
  return _window.withState.includes(id);
};

const rad = (x: number) => {
  return (x * Math.PI) / 180;
};

export const GetDistance = (p1: { lat: number; long: number }) => {
  const geoData = document.body.dataset.geoData ? JSON.parse(document.body.dataset.geoData) : null;
  const p2 = {
    lat: geoData?.latitude ? parseFloat(geoData.latitude) : 0,
    long: geoData?.longitude ? parseFloat(geoData.longitude) : 0
  };

  // Convert latitude and longitude from degrees to radians
  let R = 6371; // Radius of the Earth in kilometers
  const dLat = rad(p2.lat - p1.lat);
  const dLon = rad(p2.long - p1.long);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(rad(p1.lat)) * Math.cos(rad(p2.lat)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  document.body.dataset.metric === 'miles' ? (R = 3958.8) : (R = 6371);
  // Distance in kilometers
  return R * c; // returns the distance in meter
};

export const ConvertUsStateAbbrAndName = (input: string): string | null => {
  const toAbbr = input.length !== 2;

  const states = [
    ['Alabama', 'AL'],
    ['Alaska', 'AK'],
    ['American Samoa', 'AS'],
    ['Arizona', 'AZ'],
    ['Arkansas', 'AR'],
    ['Armed Forces Americas', 'AA'],
    ['Armed Forces Europe', 'AE'],
    ['Armed Forces Pacific', 'AP'],
    ['California', 'CA'],
    ['Colorado', 'CO'],
    ['Connecticut', 'CT'],
    ['Delaware', 'DE'],
    ['District Of Columbia', 'DC'],
    ['Florida', 'FL'],
    ['Georgia', 'GA'],
    ['Guam', 'GU'],
    ['Hawaii', 'HI'],
    ['Idaho', 'ID'],
    ['Illinois', 'IL'],
    ['Indiana', 'IN'],
    ['Iowa', 'IA'],
    ['Kansas', 'KS'],
    ['Kentucky', 'KY'],
    ['Louisiana', 'LA'],
    ['Maine', 'ME'],
    ['Marshall Islands', 'MH'],
    ['Maryland', 'MD'],
    ['Massachusetts', 'MA'],
    ['Michigan', 'MI'],
    ['Minnesota', 'MN'],
    ['Mississippi', 'MS'],
    ['Missouri', 'MO'],
    ['Montana', 'MT'],
    ['Nebraska', 'NE'],
    ['Nevada', 'NV'],
    ['New Hampshire', 'NH'],
    ['New Jersey', 'NJ'],
    ['New Mexico', 'NM'],
    ['New York', 'NY'],
    ['North Carolina', 'NC'],
    ['North Dakota', 'ND'],
    ['Northern Mariana Islands', 'NP'],
    ['Ohio', 'OH'],
    ['Oklahoma', 'OK'],
    ['Oregon', 'OR'],
    ['Pennsylvania', 'PA'],
    ['Puerto Rico', 'PR'],
    ['Rhode Island', 'RI'],
    ['South Carolina', 'SC'],
    ['South Dakota', 'SD'],
    ['Tennessee', 'TN'],
    ['Texas', 'TX'],
    ['US Virgin Islands', 'VI'],
    ['Utah', 'UT'],
    ['Vermont', 'VT'],
    ['Virginia', 'VA'],
    ['Washington', 'WA'],
    ['West Virginia', 'WV'],
    ['Wisconsin', 'WI'],
    ['Wyoming', 'WY']
  ];

  let i;

  if (toAbbr) {
    input = input.replace(/\w\S*/g, function (txt: string) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
    for (i = 0; i < states.length; i++) {
      if (states[i][0] === input) {
        return states[i][1];
      }
    }
  } else {
    input = input.toUpperCase();
    for (i = 0; i < states.length; i++) {
      if (states[i][1] === input) {
        return states[i][0];
      }
    }
  }

  return null;
};
